import React, { useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Chip, Stack, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Divider, Tooltip, CircularProgress, useTheme, useMediaQuery
} from '@mui/material';
import {
  Link as LinkIcon, Search as SearchIcon, Info as InfoIcon,
  ListAlt as ActionsIcon, Close as CloseIcon, Person as PersonIcon,
  CalendarMonth as CalendarIcon, PhoneIphone as DeviceIcon,
  LocationOn as LocationIcon, Dns as ServerIcon, Language as LangIcon,
  Schedule as TimeIcon, CheckCircle as ActiveIcon, Cancel as EndedIcon,
  Logout as DisconnectedIcon, Refresh as RefreshIcon, ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { userActionApi, UserConnexion, UserActionEntry } from '../services/userActionApi';
import { useLanguage } from '../contexts/LanguageContext';
import { blobticMessage } from '../components/BlobticMessage';
import GenericPage from '../components/GenericPage';
import GenericTable from '../components/GenericTable';
import { ZoomField } from '../components/ZoomField';
import { ZoomData } from '../services/blobticZoomService';

const fsx = {
  '& .MuiInputBase-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
};

const toLocalInput = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function formatDate(value?: string | null): string {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('fr-DZ');
  } catch {
    return value;
  }
}

function formatDuration(ms: number): string {
  if (!ms && ms !== 0) return '-';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} s`;
  return `${ms} ms`;
}

function methodColor(method: string): 'info' | 'success' | 'warning' | 'error' | 'default' {
  switch (method.toUpperCase()) {
    case 'GET': return 'info';
    case 'POST': return 'success';
    case 'PUT': case 'PATCH': return 'warning';
    case 'DELETE': return 'error';
    default: return 'default';
  }
}

function statusColor(status: number): 'success' | 'error' | 'warning' | 'info' | 'default' {
  if (status >= 500) return 'error';
  if (status >= 400) return 'warning';
  return 'success';
}

function cnxState(active?: number | null, datf?: string | null): { label: string; color: 'success' | 'error' | 'default'; icon: React.ReactElement } {
  if (active === 1) {
    return { label: 'Actif', color: 'success', icon: <ActiveIcon fontSize="small" /> };
  }
  if (active === -1) {
    return { label: 'Déconnecté', color: 'error', icon: <DisconnectedIcon fontSize="small" /> };
  }
  return { label: 'Fermé', color: 'default', icon: <EndedIcon fontSize="small" /> };
}

const ConnexionCard: React.FC<{ cnx: UserConnexion; onDetail: () => void; onActions: () => void; t: (k: string, ...a: any[]) => string }> = ({ cnx, onDetail, onActions, t }) => {
  const state = cnxState(cnx.CNX_ACTIVE, cnx.CNX_DATF);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, mb: 1.5, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
          <Chip label={`CNX #${cnx.CNX_IDF}`} size="small" color="primary" variant="outlined" />
          <Chip icon={state.icon} label={state.label} size="small" color={state.color} sx={{ fontSize: '0.7rem', height: 22 }} />
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <CalendarIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{formatDate(cnx.CNX_DATD)}</Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {cnx.CNXW_APPLICATION && <Chip label={cnx.CNXW_APPLICATION} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />}
          {(cnx.DOS_CODE || cnx.DOS_LIB) && (
            <Chip label={`${cnx.DOS_CODE || ''}${cnx.DOS_LIB ? ` · ${cnx.DOS_LIB}` : ''}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20, maxWidth: 220 }} />
          )}
          <Chip label={`${cnx.ActionCount ?? 0} ${t('userconnex.actions')}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" color="primary" startIcon={<InfoIcon fontSize="small" />} onClick={onDetail} fullWidth>
            {t('userconnex.detail')}
          </Button>
          <Button size="small" variant="contained" color="primary" startIcon={<ActionsIcon fontSize="small" />} onClick={onActions} fullWidth>
            {t('userconnex.load_actions')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export const UserConnexionsPage: React.FC = () => {
  const { t } = useLanguage();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // View mode: 'list' (filters + connexions) or 'actions' (actions of one connexion)
  const [view, setView] = useState<'list' | 'actions'>('list');

  const [selectedUser, setSelectedUser] = useState<ZoomData | null>(null);
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return toLocalInput(d);
  });
  const [endDate, setEndDate] = useState<string>(() => toLocalInput(new Date()));
  const [connexions, setConnexions] = useState<UserConnexion[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailCnx, setDetailCnx] = useState<UserConnexion | null>(null);

  // Actions view state
  const [activeCnx, setActiveCnx] = useState<UserConnexion | null>(null);
  const [actions, setActions] = useState<UserActionEntry[]>([]);
  const [loadingActions, setLoadingActions] = useState(false);

  const load = useCallback(async () => {
    if (!selectedUser || !selectedUser.USR_IDF) {
      blobticMessage.error(t('userconnex.error_no_user'), '');
      return;
    }
    setLoading(true);
    try {
      const data = await userActionApi.getConnexions(Number(selectedUser.USR_IDF), startDate, endDate);
      setConnexions(data || []);
      setLoaded(true);
    } catch (e: any) {
      blobticMessage.error(t('userconnex.error_load'), e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, startDate, endDate, blobticMessage.error, t]);

  const openActions = useCallback(async (cnx: UserConnexion) => {
    setActiveCnx(cnx);
    setActions([]);
    setView('actions');
    setLoadingActions(true);
    try {
      const data = await userActionApi.getConnexionActions(cnx.CNX_IDF);
      setActions(data || []);
    } catch (e: any) {
      blobticMessage.error(t('userconnex.error_load_actions'), e?.response?.data?.message || e.message);
    } finally {
      setLoadingActions(false);
    }
  }, [blobticMessage.error, t]);

  const reloadActions = useCallback(async () => {
    if (!activeCnx) return;
    setLoadingActions(true);
    try {
      const data = await userActionApi.getConnexionActions(activeCnx.CNX_IDF);
      setActions(data || []);
    } catch (e: any) {
      blobticMessage.error(t('userconnex.error_load_actions'), e?.response?.data?.message || e.message);
    } finally {
      setLoadingActions(false);
    }
  }, [activeCnx, blobticMessage.error, t]);

  const backToList = () => {
    setView('list');
    setActiveCnx(null);
    setActions([]);
  };

  const handleZoomChange = (_val: string, data?: ZoomData) => {
    setSelectedUser(data || null);
  };

  // ─── ACTIONS VIEW ─────────────────────────────────────────────────────────

  if (view === 'actions' && activeCnx) {
    const state = cnxState(activeCnx.CNX_ACTIVE, activeCnx.CNX_DATF);
    return (
      <GenericPage
        title={t('userconnex.actions_title')}
        icon={<ActionsIcon />}
        footerEnabled={false}
        additionalActions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={backToList} sx={{ borderRadius: 2 }}>
              {t('userconnex.back_to_list')}
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={reloadActions} sx={{ borderRadius: 2 }}>
              {t('userconnex.refresh')}
            </Button>
          </Stack>
        }
      >
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2.5, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip label={`CNX #${activeCnx.CNX_IDF}`} size="small" color="primary" variant="outlined" />
              <Chip icon={state.icon} label={state.label} size="small" color={state.color} />
              {activeCnx.CNXW_APPLICATION && <Chip label={activeCnx.CNXW_APPLICATION} size="small" variant="outlined" />}
              {(activeCnx.DOS_CODE || activeCnx.DOS_LIB) && (
                <Chip label={`${activeCnx.DOS_CODE || ''}${activeCnx.DOS_LIB ? ` · ${activeCnx.DOS_LIB}` : ''}`} size="small" variant="outlined" />
              )}
              {activeCnx.USR_MAIL && <Chip label={activeCnx.USR_MAIL} size="small" variant="outlined" />}
            </Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('userconnex.table.start')}</Typography>
                <Typography variant="body2" fontWeight={500}>{formatDate(activeCnx.CNX_DATD)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('userconnex.table.end')}</Typography>
                <Typography variant="body2" fontWeight={500}>{formatDate(activeCnx.CNX_DATF)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('userconnex.table.actions_count')}</Typography>
                <Typography variant="body2" fontWeight={500}>{actions.length}</Typography>
              </Box>
            </Stack>
          </Stack>
        </Paper>

        {loadingActions ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : actions.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="body1" color="text.secondary">{t('userconnex.actions_empty')}</Typography>
          </Paper>
        ) : (
          <GenericTable
            columns={[
              { id: 'UAD_START', label: t('userconnex.table.date'), minWidth: 150, format: (v: any) => formatDate(v) },
              {
                id: 'UAD_METHOD', label: t('userconnex.table.method'), minWidth: 80,
                format: (v: any) => <Chip label={v} size="small" color={methodColor(v)} variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
              },
              {
                id: 'UAD_PATH', label: t('userconnex.table.path'), minWidth: 260,
                format: (v: any, row?: any) => (
                  <Box>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{v}</Typography>
                    {row?.UAD_OKEY && (
                      <Chip label={row.UAD_OKEY} size="small" variant="outlined" color="info" sx={{ fontSize: '0.6rem', height: 18, mt: 0.5, maxWidth: 380 }} />
                    )}
                  </Box>
                )
              },
              {
                id: 'UAD_STATUS', label: t('userconnex.table.status'), minWidth: 75,
                format: (v: any) => <Chip label={v} size="small" color={statusColor(v)} sx={{ fontSize: '0.7rem', height: 20 }} />
              },
              {
                id: 'UAD_DURATION_MS', label: t('userconnex.table.duration'), minWidth: 90,
                format: (v: any) => <Typography variant="body2">{formatDuration(v)}</Typography>
              },
            ]}
            data={actions}
            searchPlaceholder={t('userconnex.search_placeholder')}
            maxHeight="calc(100vh - 460px)"
          />
        )}
      </GenericPage>
    );
  }

  // ─── LIST VIEW ─────────────────────────────────────────────────────────────

  const infoRows: { label: string; value?: string | number | null; icon?: React.ReactNode }[] = detailCnx ? [
    { label: t('userconnex.field.num'), value: detailCnx.CNX_NUM, icon: <TimeIcon fontSize="small" /> },
    { label: t('userconnex.field.start'), value: formatDate(detailCnx.CNX_DATD), icon: <CalendarIcon fontSize="small" /> },
    { label: t('userconnex.field.end'), value: formatDate(detailCnx.CNX_DATF), icon: <CalendarIcon fontSize="small" /> },
    { label: t('userconnex.field.last_activity'), value: formatDate(detailCnx.CNXW_DATF), icon: <TimeIcon fontSize="small" /> },
    { label: t('userconnex.field.app'), value: detailCnx.CNXW_APPLICATION, icon: <LinkIcon fontSize="small" /> },
    { label: t('userconnex.field.dossier'), value: detailCnx.DOS_IDF ? `${detailCnx.DOS_CODE || ''}${detailCnx.DOS_LIB ? ` · ${detailCnx.DOS_LIB}` : ''}` : null, icon: <LinkIcon fontSize="small" /> },
    { label: t('userconnex.field.ip'), value: detailCnx.CNXW_REMOTEIP, icon: <LocationIcon fontSize="small" /> },
    { label: t('userconnex.field.host'), value: detailCnx.CNXW_HOST, icon: <ServerIcon fontSize="small" /> },
    { label: t('userconnex.field.computer'), value: detailCnx.CNX_COMPUTERNAME, icon: <DeviceIcon fontSize="small" /> },
    { label: t('userconnex.field.language'), value: detailCnx.CNXW_USR_LANGUAGE || detailCnx.CNXW_LANGUAGE, icon: <LangIcon fontSize="small" /> },
    { label: t('userconnex.field.referer'), value: detailCnx.CNXW_REFER, icon: <ServerIcon fontSize="small" /> },
    { label: t('userconnex.field.wid'), value: detailCnx.CNXW_WID, icon: <DeviceIcon fontSize="small" /> },
    { label: t('userconnex.field.text'), value: detailCnx.CNX_TEXT, icon: <InfoIcon fontSize="small" /> },
  ] : [];

  const state = detailCnx ? cnxState(detailCnx.CNX_ACTIVE, detailCnx.CNX_DATF) : null;

  return (
    <GenericPage
      title={t('userconnex.title')}
      icon={<LinkIcon />}
      footerEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} sx={{ borderRadius: 2 }}>
            {t('userconnex.refresh')}
          </Button>
        </Stack>
      }
    >
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2.5, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <ZoomField
              zoomKey="user"
              label={t('userconnex.filter.user')}
              placeholder={t('userconnex.filter.user_placeholder')}
              value={selectedUser?.USR_CODE || ''}
              displayField="USR_CODE"
              valueField="USR_IDF"
              onChange={handleZoomChange}
              fullWidth
              size="small"
              sx={fsx}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label={t('userconnex.filter.start')}
              type="datetime-local"
              size="small"
              fullWidth
              sx={fsx}
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label={t('userconnex.filter.end')}
              type="datetime-local"
              size="small"
              fullWidth
              sx={fsx}
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<SearchIcon />}
              onClick={load}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              {t('userconnex.filter.load')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={32} />
        </Box>
      ) : loaded && connexions.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="body1" color="text.secondary">{t('userconnex.no_data')}</Typography>
        </Paper>
      ) : connexions.length > 0 ? (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {selectedUser?.USR_LIB || selectedUser?.USR_MAIL || ''}
            </Typography>
            <Chip label={t('userconnex.filter.count', connexions.length)} color="primary" variant="outlined" />
          </Stack>
          {isMobile ? (
            <Box>
              {connexions.map(cnx => (
                <ConnexionCard
                  key={cnx.CNX_IDF}
                  cnx={cnx}
                  t={t}
                  onDetail={() => setDetailCnx(cnx)}
                  onActions={() => openActions(cnx)}
                />
              ))}
            </Box>
          ) : (
            <GenericTable
              columns={[
                { id: 'CNX_IDF', label: t('userconnex.table.cnx'), minWidth: 80, format: (v: any) => <Chip label={`#${v}`} size="small" color="primary" variant="outlined" /> },
                { id: 'CNX_DATD', label: t('userconnex.table.start'), minWidth: 160, format: (v: any) => formatDate(v) },
                { id: 'CNX_DATF', label: t('userconnex.table.end'), minWidth: 160, format: (v: any) => formatDate(v) },
                {
                  id: 'CNX_ACTIVE', label: t('userconnex.table.state'), minWidth: 120,
                  format: (v: any, row?: any) => {
                    const s = cnxState(v, row?.CNX_DATF);
                    return <Chip icon={s.icon} label={s.label} size="small" color={s.color} sx={{ fontSize: '0.7rem', height: 22 }} />;
                  }
                },
                {
                  id: 'CNXW_APPLICATION', label: t('userconnex.table.app'), minWidth: 110,
                  format: (v: any) => v ? <Chip label={v} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} /> : '-'
                },
                {
                  id: 'DOS_CODE', label: t('userconnex.table.dossier'), minWidth: 150,
                  format: (v: any, row?: any) => (row?.DOS_CODE || row?.DOS_LIB)
                    ? `${row.DOS_CODE || ''}${row.DOS_LIB ? ` · ${row.DOS_LIB}` : ''}`
                    : '-'
                },
                {
                  id: 'ActionCount', label: t('userconnex.table.actions_count'), minWidth: 90,
                  format: (v: any) => <Chip label={String(v ?? 0)} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                },
              ]}
              data={connexions}
              searchPlaceholder={t('userconnex.search_placeholder')}
              rowActions={(row) => (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title={t('userconnex.detail')}>
                    <IconButton size="small" color="primary" onClick={() => setDetailCnx(row)}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('userconnex.load_actions')}>
                    <IconButton size="small" color="primary" onClick={() => openActions(row)}>
                      <ActionsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
              maxHeight="calc(100vh - 480px)"
            />
          )}
        </>
      ) : null}

      {/* Connexion detail dialog */}
      <Dialog open={!!detailCnx} onClose={() => setDetailCnx(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {t('userconnex.details_title')}
            {detailCnx && <Chip label={`CNX #${detailCnx.CNX_IDF}`} size="small" color="primary" variant="outlined" />}
          </Stack>
          <IconButton size="small" onClick={() => setDetailCnx(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailCnx && state && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip icon={state.icon} label={state.label} size="small" color={state.color} />
                {detailCnx.USR_MAIL && <Chip label={detailCnx.USR_MAIL} size="small" variant="outlined" />}
                {detailCnx.USR_LIB && <Chip label={detailCnx.USR_LIB} size="small" variant="outlined" />}
              </Stack>
              <Divider />
              <Stack spacing={1}>
                {infoRows.filter(r => r.value !== null && r.value !== '' && r.value !== undefined).map((r, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {r.icon}
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130 }}>{r.label}</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', fontWeight: 500 }}>{r.value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailCnx(null)} color="inherit">{t('userconnex.close')}</Button>
        </DialogActions>
      </Dialog>
    </GenericPage>
  );
};

export default UserConnexionsPage;
